import {
	Button, ButtonGroup, Card, Classes, ControlGroup, Elevation, InputGroup, ITreeNode,
	Menu, MenuItem, Popover, Position, Switch, Tree
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import autobind from "autobind-decorator";
import React from 'react';
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import styled from 'styled-components';

import * as editActions from "core/actions/editActions";
import initialState from "core/reducers/initialState";
import IPropertyRecord from "core/util/property/IPropertyRecord";
import IPropertyReference from "core/util/property/IPropertyReference";
import IPropertyType from "core/util/property/IPropertyType";
import { PropertyProvider } from "core/util/property/PropertyProvider";
import PropertyTypeRegistry from "core/util/property/PropertyTypeRegistry";

interface IPropertyWrapper<T> extends IPropertyRecord<T> {
	id: string;
}

type PropertyWrapper = { id: string } & IPropertyRecord<any>;

export interface IPropertyListProps {
	properties?: Array<{ id: string } & IPropertyRecord<any>>;
	onClick: (type: IPropertyType<any, any>, reference: IPropertyReference) => void;
	scope: string;
	group: string;
	type?: string; // restrict by type
	renderHeader?: (header: React.ReactNode) => void;
	headerUpdated?: () => void;
}

interface IFilter {
	types: string[];
	query: string;
}

interface IPropertyListState {
	display: "list" | "tree";
	filter: IFilter;
}

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	flex-grow: 1;

	& > *:first-child {
		margin-right: 15px;
	}
`;

const FilterCont = styled.div`
	display: flex;

	& > *:first-child {
		flex: 1;
	}
`;

const ListItem = styled.li`
	margin-top: 10px;
	cursor: pointer;
`;

class PropertyList extends React.Component<IPropertyListProps, IPropertyListState> {

	constructor(props) {
		super(props);

		this.state = {
			display: "list",
			filter: {types: [], query: null}
		};
	}

	@autobind
	private filterList(properties: Array<IPropertyRecord<any>>, filter: IFilter) {
		const { types, query } = filter;

		let filtered = properties;

		if(this.props.type) {
			filtered = filtered.filter((p) => this.props.type === p.type);
		} else if (types) {
			filtered = filtered.filter((p) => (
				types.indexOf(p.type)
			));
		}

		if(query) {
			filtered = filtered.filter((p) => (
				p.displayName && p.displayName.includes(query)
			));
		}

		return filtered;
	}

	@autobind
	private renderListItem<T>(property: IPropertyWrapper<T>, index?: number) {
		const type = PropertyTypeRegistry.get(property.type);

		// hard coded to properties scope for now
		const ref = PropertyProvider.setAsReference({
			scope: this.props.scope,
			group: this.props.group,
			field: property.id
		});

		return (
			<ListItem
				onClick={() => this.props.onClick(type, ref)}
				key={property.id}
				className="bp3-popover-dismiss"
			>
				{type.getPreviewElement(property.value ? property.value : type.getDefaultValue(), false, property)}
			</ListItem>
		);
	}

	private renderList(properties: Array<IPropertyRecord<any>> = [], filter: IFilter) {
		return (
			<ul>
				{this.filterList(properties, filter).map(this.renderListItem)}
			</ul>
		);
	}

	private renderTree(properties: Array<IPropertyRecord<any>> = [], filter: IFilter) {
		const types = PropertyTypeRegistry.getAll().filter((t) => !filter.types.includes(t.name));

		const nodes: ITreeNode[] = types.map((t) => ({
			id: t.name,
			label: t.label,
			hasCaret: true,
			chileNodes: this.filterList(properties, {...filter, types: [t.name]}).map((p) => ({
				id: p.displayName,
				label: JSON.stringify(t)
			}))
		}));

		return (
			<React.Fragment>
				<Tree
					contents={nodes}
				/>
			</React.Fragment>
		);
	}

	@autobind
	private renderFilter() {
		const { display, filter, filter: {types, query}} = this.state;

		// construct menu of property types
		const typeOptoins = PropertyTypeRegistry.getAll().map((t) => (
			<MenuItem key={t.name} text={
				<Switch
					checked={!types.includes(t.name)}
					onChange={(event: any) => {
						this.setState({
							filter: {...filter, types: !event.target.checked ? [...types, t.name] : types.filter((f) => t.name !== f)}
						});
					}}
					label={t.label}
					className={Classes.POPOVER_DISMISS_OVERRIDE}
					style={{marginBottom: 0}}
				/>
			} />
		));

		const menu = ( <Menu> {typeOptoins} </Menu> );

		return (
			<FilterCont>
					<ControlGroup vertical={false}>
						{!this.props.type && (
							<Popover content={menu} position={Position.BOTTOM} usePortal={false}>
								<Button icon={IconNames.FILTER_LIST} text="Types" minimal={true} />
							</Popover>
						)}

						<InputGroup
							leftIcon={IconNames.SEARCH}
							onChange={(e) => this.setState({
								filter: {...filter, query: e.target.value}
							})}
							placeholder="Filter properties"
							value={query || ""}
						/>

						<Button icon={IconNames.CROSS}  minimal={true} onClick={() => this.setState({
							filter: {query: null, types: []}
						})} />
					</ControlGroup>
			</FilterCont>
		);
	}

	public componentDidMount() {
		const { display, filter } = this.state;

		const displaySelect = (
			<ButtonGroup>
				<Button
					icon={IconNames.LIST}
					onClick={() => this.setState({display: "list"}, this.props.headerUpdated)}
					disabled={"list" === display}
				/>
				<Button
					icon={IconNames.DIAGRAM_TREE}
					onClick={() => this.setState({display: "tree"}, this.props.headerUpdated)}
					disabled={"tree" === display}
				/>
			</ButtonGroup>
		);

		this.props.renderHeader(
			<Header>
				{displaySelect}
				{this.renderFilter()}
			</Header>
		);
	}

	public render() {
		const { display, filter } = this.state;

		return (
			<React.Fragment>
				{"list" === display && this.renderList(this.props.properties, filter)}
				{"tree" === display && this.renderTree(this.props.properties, filter)}
			</React.Fragment>
		);
	}
}

function mapProps({ config }: typeof initialState, {scope, group}: IPropertyListProps) {
	const properties = Object.entries(config[scope][group]);

	return {
		properties:  properties.map(([id, prop]) => {
			return {...prop, id};
		})
	};
}

function mapActions(dispatch: Dispatch<typeof initialState>) {
	return {
		actions: bindActionCreators(editActions, dispatch)
	};
}

export default connect(mapProps, mapActions)(PropertyList);



















// WEBPACK FOOTER //
// ./src/core/components/editor/properties/PropertyList.tsx